(ns frontend.ui.page.lp-report-editor.sub
  (:require
   [clojure.edn :as edn]
   [re-frame.core :as rf]
   [common.schema.entities.lp-portal.lp-report-holding :as lp-report-holding]
   [clojure.string :as string]
   [common.util :as cu]
   [common.schema.entities.lp-portal.lp-report :as lp-report]
   [frontend.ui.page.lp-portal.report.controller :as report-controller]))


(defn parse-data-status
  "Read a report-data-status value into a map; it arrives as a pr-str string."
  [value]
  (cond
    (map? value) value
    (string? value) (try
                      (edn/read-string value)
                      (catch :default _ nil))
    :else nil))

(def tenant-path [:tenant])

(def account-id-path [:account-id])
(def team-memberships-ids-path [:account-team-ids])
(def editor-path [:lp-report-editor])
(def initialized-path (conj editor-path :initialized?))
(def reloading-path (conj editor-path :reloading?))
(def import-history-path (conj editor-path :import-history))
(def close-to-fund-path (conj editor-path :close-to-fund-id))
(def active-step-path (conj editor-path :active-step))
(def errors-path (conj editor-path :errors))
(def reporting-admin?-path (conj editor-path :reporting-admin?))
(def fund-path (conj editor-path :fund))
(def report-path (conj editor-path :lp-report))

(def report-images-path (conj editor-path :images))
(def attachments-path (conj editor-path :lp-report-attachments))
(def holdings-path (conj editor-path :lp-report-holdings))
(def investor-contacts-path (conj editor-path :lp-report-investor-contacts))
(def investor-commitments-path (conj editor-path :lp-report-investor-commitments))
(def feeder-investor-contacts-path (conj editor-path :lp-report-feeder-investor-contacts))
(def feeder-investor-commitments-path (conj editor-path :lp-report-feeder-investor-commitments))
(def feeder-funds-path (conj editor-path :lp-report-feeder-funds))
(def feeder-reports-path (conj editor-path :lp-report-feeder-reports))

(def accounts-path (conj editor-path :accounts))

(rf/reg-sub
 ::initialized?
 (fn [db]
   (get-in db initialized-path)))

(rf/reg-sub
 ::accounts
 (fn [db]
   (get-in db [:lp-report-editor :accounts])))

(defn- account->option
  [{:keys [db/id account/first-name account/last-name]}]
  [id (string/join " " [first-name last-name])])

(rf/reg-sub
 ::accounts-as-options
 :<-
 [::accounts]
 (fn [accounts]
   (some->> accounts
            (map account->option)
            (into {}))))

(rf/reg-sub
 ::full-access?
 (fn [db]
   (let [report (get-in db report-path)
         admin? (get-in db reporting-admin?-path)
         owner? (some #{(get-in db account-id-path)}
                      (:lp-report/process-owners report))]
     (lp-report/can-process? report (or owner? admin?)))))

(rf/reg-sub
 ::report-admin?
 (fn [db]
   (get-in db reporting-admin?-path)))

(rf/reg-sub
 ::lp-report
 (fn [db]
   (let [report (get-in db report-path)]
     report)))

;; As clearly stated in re-frame's documentation:
;; > Views should only compute hiccup. A view shouldn't process input data. The
;; > subscriptions it uses should deliver the data already in the right
;; > structure, ready for use in hiccup generation.
;; [0] https://day8.github.io/re-frame/correcting-a-wrong/
;;
;; Views dictate the structure of the input they are passed and Controller must
;; abide. This validates the mindset to have when developing:
;; - first develop the View in isolation from the Controller: during this
;;   cycle, we define the input structure that suits the View
;; - then develop the Controller that picks data from the Model and feeds it
;;   to said View (taking care of converting it to the format used by the View)
;;
;; Here, we need to turn accounts into lists of ids (`:process-owners` into
;; `:process-owner-ids` etc …).

(rf/reg-sub
 ::active-step
 (fn [db]
   (let [as (get-in db active-step-path :lp-report.step/initialization)]
     as)))

(rf/reg-sub
 ::feeder-access?
 (fn [db]
   (let [account-id (get-in db account-id-path)
         teams (get-in db team-memberships-ids-path)
         acc-and-teams (->> (concat [account-id] teams)
                            (remove nil?)
                            (set))
         report (get-in db report-path)
         feeder-reports (get-in db feeder-reports-path)
         report-participants (->> (concat
                                   (:lp-report/process-owners report)
                                   (:lp-report/followers report)
                                   [(:lp-report/final-approver report)]
                                   (->> feeder-reports
                                        (mapcat (fn [feeder]
                                                  (concat (:lp-report/text-contributors feeder)
                                                          (:lp-report/data-contributors feeder)
                                                          [(:lp-report/data-approver feeder)
                                                           (:lp-report/text-approver feeder)])))))
                                  (remove nil?)
                                  (set))]
     (or (get-in db reporting-admin?-path)
         (some acc-and-teams
               report-participants)))))

(rf/reg-sub
 ::master-report-access?
 (fn [db]
   (let [account-id (get-in db account-id-path)
         teams (get-in db team-memberships-ids-path)
         acc-and-teams (->> (concat [account-id] teams)
                            (remove nil?)
                            (set))
         report (get-in db report-path)
         report-participants (->> (concat
                                   (:lp-report/process-owners report)
                                   (:lp-report/followers report)
                                   (:lp-report/text-contributors report)
                                   (:lp-report/data-contributors report)
                                   [(:lp-report/final-approver report)
                                    (:lp-report/text-approver report)
                                    (:lp-report/data-approver report)])
                                  (remove nil?)
                                  (set))]
     (or (get-in db reporting-admin?-path)
         (some acc-and-teams
               report-participants)))))

(rf/reg-sub
 ::holdings-access?
 (fn [db]
   (let [account-id (get-in db account-id-path)
         teams (get-in db team-memberships-ids-path)
         acc-and-teams (->> (concat [account-id] teams)
                            (remove nil?)
                            (set))
         report (get-in db report-path)
         holding-reports (get-in db holdings-path)
         report-participants
         (->> (concat
               (:lp-report/process-owners report)
               (:lp-report/followers report)
               (:lp-report/text-contributors report)
               (:lp-report/data-contributors report)
               [(:lp-report/final-approver report)
                (:lp-report/data-approver report)
                (:lp-report/text-approver report)]
               (->> holding-reports
                    (vals)
                    (mapcat (fn [h]
                              (concat (:lp-report-holding/text-contributors h)
                                      (:lp-report-holding/data-contributors h)
                                      [(:lp-report-holding/text-approver h)
                                       (:lp-report-holding/data-approver h)])))))
              (remove nil?)
              (set))]
     (or (get-in db reporting-admin?-path)
         (some acc-and-teams
               report-participants)))))

(rf/reg-sub
 ::has-access?
 (fn [db]
   (let [account-id (get-in db account-id-path)
         teams (get-in db team-memberships-ids-path)
         acc-and-teams (->> (concat [account-id] teams)
                            (remove nil?)
                            (set))
         report (get-in db report-path)
         feeder-reports-path (get-in db feeder-reports-path)
         holding-reports (get-in db holdings-path)
         report-participants
         (some->> (concat
                   (:lp-report/text-contributors report)
                   (:lp-report/data-contributors report)
                   (:lp-report/process-owners report)
                   (:lp-report/followers report)
                   [(:lp-report/final-approver report)
                    (:lp-report/text-approver report)
                    (:lp-report/data-approver report)]
                   (some->> feeder-reports-path
                            (mapcat (fn [feeder]
                                      (concat (:lp-report/text-contributors feeder)
                                              (:lp-report/data-contributors feeder)
                                              [(:lp-report/data-approver feeder)
                                               (:lp-report/text-approver feeder)]))))
                   (some->> holding-reports
                            (vals)
                            (mapcat (fn [h]
                                      (concat (:lp-report-holding/text-contributors h)
                                              (:lp-report-holding/data-contributors h)
                                              [(:lp-report-holding/text-approver h)
                                               (:lp-report-holding/data-approver h)])))))
                  (remove nil?)
                  (set))]
     (or (get-in db reporting-admin?-path)
         (some acc-and-teams
               report-participants)))))

(rf/reg-sub
 ::lp-report-attachments
 (fn [db [_ _report-id]]
   (get-in db attachments-path)))

(rf/reg-sub
 ::lp-report-investor-contacts
 (fn [db]
   (get-in db investor-contacts-path)))

(rf/reg-sub
 ::lp-report-investor-commitments
 (fn [db]
   (get-in db investor-commitments-path)))

(rf/reg-sub
 ::lp-report-feeder-investor-contacts
 (fn [db]
   (get-in db feeder-investor-contacts-path)))

(rf/reg-sub
 ::lp-report-feeder-investor-commitments
 (fn [db]
   (get-in db feeder-investor-commitments-path)))

(rf/reg-sub
 ::associated-contact-person
 (fn [db [_ association-id]]
   (or (some->> (get-in db investor-contacts-path)
                (mapcat (fn [[_ contacts]]
                          (->> contacts
                               (map :reporting-contacts)
                               (keep (fn [m]
                                       (get m association-id))))))
                (first))
       (some->> (get-in db feeder-investor-contacts-path)
                (mapcat (fn [[_ contacts]]
                          (->> contacts
                               (map :reporting-contacts)
                               (keep (fn [m]
                                       (get m association-id))))))
                (first)))))

(rf/reg-sub
 ::lp-report-holdings
 (fn [db]
   (let [holdings (get-in db holdings-path)]
     holdings)))

(rf/reg-sub
 ::holding-templates
 (fn [db]
   (let [tenant-templates (get-in db [:tenant :tenant/reporting-holding-templates])
         sorted (->> lp-report-holding/hardcoded-holding-templates
                     (filter (fn [[k _l]]
                               (some #{k} tenant-templates)))
                     (sort-by (fn [[_ l]]
                                (cu/str-trim-lcase l))))]
     sorted)))

(rf/reg-sub
 ::holding-contributors
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::account-id
 (fn [db]
   (get-in db account-id-path)))

(rf/reg-sub
 ::fund
 (fn [db]
   (get-in db fund-path)))

(rf/reg-sub
 ::holding-approvers
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::report-owners
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::data-contributors
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::text-contributors
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::followers
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(defn get-main-or-feeder-report
  [master-report db report-id]
  (if (= report-id (:db/id master-report))
    master-report
    (->> (get-in db feeder-reports-path)
         (cu/filter-by :db/id report-id)
         (first))))

(rf/reg-sub
 ::report
 (fn [db [_ report-id]]
   (let [master-report (get-in db report-path)]
     (get-main-or-feeder-report master-report db report-id))))

(rf/reg-sub
 ::lp-report-data-file
 (fn [db [_ report-id]]
   (let [attachment-id (if (= report-id (get-in db (conj report-path :db/id)))
                         (get-in db (conj report-path :lp-report/report-data-file))
                         (->> (get-in db feeder-reports-path)
                              (cu/filter-by :db/id report-id)
                              (first)
                              :lp-report/report-data-file))]
     (when attachment-id
       (get-in db (conj attachments-path attachment-id))))))

(rf/reg-sub
 ::final-approvers
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::text-approvers
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::data-approvers
 :<-
 [::accounts-as-options]
 (fn [account-options]
   account-options))

(rf/reg-sub
 ::fund-templates
 (fn [db]
   (let [tenant-templates (get-in db [:tenant :tenant/reporting-fund-templates])]
     (->> lp-report/hardcoded-fund-templates
          (filter (fn [[k _l]]
                    (some #{k} tenant-templates)))))))

(rf/reg-sub
 ::feeder-fund-templates
 :<-
 [::fund-templates]
 (fn [templates]
   (->> templates
        (filter (fn [[k _]]
                  (lp-report/feeder-template? k)))
        (sort-by second))))

(rf/reg-sub
 ::feeder-reports
 (fn [db]
   (seq (get-in db feeder-reports-path))))

(rf/reg-sub
 ::feeder-funds
 (fn [db]
   (get-in db feeder-funds-path)))

(rf/reg-sub
 ::feeder-in-use?
 :<-
 [::feeder-funds]
 (fn [feeders]
   (boolean (seq feeders))))


(rf/reg-sub
 ::feeder-fund-options
 :<-
 [::feeder-funds]
 (fn [feeders]
   (->> feeders
        (map (fn [[id feeder]]
               [id (:fund/name feeder)]))
        (sort-by second))))

;; A discontinued fund cannot be added as a feeder. The display still uses
;; ::feeder-fund-options, so an earlier feeder keeps its name.
(rf/reg-sub
 ::selectable-feeder-fund-options
 :<-
 [::feeder-funds]
 (fn [feeders]
   (->> feeders
        (remove (fn [[_id feeder]]
                  (:fund/reporting-discontinued? feeder)))
        (map (fn [[id feeder]]
               [id (:fund/name feeder)]))
        (sort-by second))))

(rf/reg-sub
 ::fund-report-ui-data
 (fn [db [_ report-id]]
   (let [master-report (get-in db report-path)
         report (if (= report-id (:db/id master-report))
                  master-report
                  (->> (get-in db feeder-reports-path)
                       (cu/filter-by :db/id report-id)
                       (first)))
         report-images (get-in db report-images-path)]
     (report-controller/report->fund-ui-data {:report report
                                              :images report-images}))))

(rf/reg-sub
 ::holding-report-ui-data
 (fn [db [_ holding-id]]
   (let [holding-report (get-in db (conj holdings-path holding-id))
         report-images (get-in db report-images-path)
         ui-data (report-controller/holding-report->ui-data {:report holding-report
                                                             :images report-images})]
     ui-data)))

(rf/reg-sub
 ::feeder-report-ui-data
 (fn [db [_ feeder-id]]
   (let [report (->> (get-in db feeder-reports-path)
                     (cu/filter-by :db/id feeder-id)
                     (first))
         report-images (get-in db report-images-path)]
     (report-controller/report->fund-ui-data {:report report
                                              :images report-images}))))

(rf/reg-sub
 ::reports-not-approved?
 (fn [db]
   (let [report (get-in db report-path)
         holdings (get-in db holdings-path)
         fund-report-not-approved? (and (not= :lp-report-template/materials-only
                                              (:lp-report/template-type report))
                                        (or (not= :lp-report.publishing-status/approved
                                                  (:lp-report/text-status report))
                                            (not= :lp-report.publishing-status/approved
                                                  (:lp-report/data-status report))))
         holding-reports-not-approved?
         (->> report
              :lp-report/holdings
              (some (fn [holding-id]
                      (let [holding-report (get holdings holding-id)]
                        (and (not= :lp-report-holding-template/no-reporting
                                   (:lp-report-holding/template-type holding-report))
                             (or (not= :lp-report-holding.publishing-status/approved
                                       (:lp-report-holding/text-status
                                        holding-report))
                                 (not= :lp-report-holding.publishing-status/approved
                                       (:lp-report-holding/data-status
                                        holding-report))))))))]
     (or fund-report-not-approved?
         holding-reports-not-approved?))))

(rf/reg-sub
 ::feeder-not-approved?
 (fn [db]
   (->> (get-in db feeder-reports-path)
        (some (fn [feeder-report]
                (or (not= :lp-report.publishing-status/approved
                          (:lp-report/text-status feeder-report))
                    (not= :lp-report.publishing-status/approved
                          (:lp-report/data-status feeder-report))))))))

(rf/reg-sub
 ::burn-pdfs-processing?
 (fn [db [_]]
   (get-in db [::burn-pdfs-processing?])))

(rf/reg-sub
 ::materials-selected-tab
 (fn [db [_ report-id]]
   (get-in db [:materials-selected-tab report-id] :all)))

(rf/reg-sub
 ::import-history
 (fn [db _]
   (get-in db import-history-path)))
